import { isLoaderActionError } from '@findigs/helpers/src/helpers/loader-action-error';
import { useIsDevUser } from '@findigs/helpers/src/hooks/use-is-dev-user';
import { Button } from '@findigs/ui-kit/src/components/button';
import {
  isRouteErrorResponse,
  useAsyncError,
  useRouteError,
} from '@remix-run/react';
import {
  captureException,
  captureRemixErrorBoundaryError,
} from '@sentry/remix';
import { DevErrorView } from '~/components/dev-error-view';
import { ErrorPageLayout } from '~/components/error-page-layout';
import { RouteErrorView } from '~/components/route-error-view';
import type { LoaderActionError, RouteError } from '~/types';

/**
 * Root error boundary for the application
 * This should only be exported in root.tsx
 *
 * @returns JSX for the Error UI wrapped in the minimal layout
 */
export function RootErrorBoundary() {
  const devUser = useIsDevUser();

  const error = useRouteError();
  const asyncError = useAsyncError();

  if (error) captureRemixErrorBoundaryError(error);
  if (asyncError) captureException(asyncError);

  // Overlay with stack trace to help developers debug
  const dev = devUser ? (
    <DevErrorView error={error ?? asyncError} className="pt-12" />
  ) : null;

  const errorContent =
    isRouteErrorResponse(error) || isLoaderActionError(error) ? (
      <>
        <RouteErrorView error={error as RouteError | LoaderActionError} />
        {dev}
      </>
    ) : (
      <>
        <h1 className="text-d-sm sm:text-d-md md:text-d-md mb-6">
          Error occurred
        </h1>
        <p className="mb-12 md:text-b-lg-regular">
          Something went wrong, please reach out to{' '}
          <a href="mailto:pm-support@findigs.com">pm-support@findigs.com</a> or
          try again.
        </p>
        <Button as="a" href="/" variant="Tonal">
          Back to home
        </Button>
        {dev}
      </>
    );

  return <ErrorPageLayout>{errorContent}</ErrorPageLayout>;
}
